import {SelectionModel} from "@angular/cdk/collections";
import {AfterViewInit, Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatTableDataSource} from "@angular/material/table";
import {lastValueFrom, map, Observable, startWith} from "rxjs";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {PageOffering} from "@core/model/side-nav.model";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {TabManagementService} from "@core-services/tab-management.service";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {
  CreateARetailerService
} from "../../../leads/retailers/create-a-retailer/create-a-lease-dialog/create-a-retailer.service";
import {
  ApplicationAssessmentStatus,
  RegisterRetailer,
  Retailer,
  TenantType
} from "../../../leads/retailers/retailers.model";
import {RetailersService} from "../../../leads/retailers/retailers.service";
import {LeaseSubLeasesService} from "../../lease-view/lease-sub-leases/lease-sub-leases.service";
import {LeaseViewComponent} from "../../lease-view/lease-view.component";
import {
  AgreementType,
  LeaseGridSpace,
  LeaseSpaceForTerm,
  LeaseSummary,
  OccupancyType,
  SubLeaseRequest
} from "../../leasing.model";
import {ConsoleUserService} from "@core-services/console-user.service";
import {RegisterUserRequest} from "@ui-kit/framework/security/model/user.model";

@Component({
  selector: 'app-create-a-aub-lease-dialog',
  templateUrl: './create-a-sub-lease-dialog.component.html',
  styleUrls: ['./create-a-sub-lease-dialog.component.scss']
})
export class CreateASubLeaseDialogComponent implements OnInit, AfterViewInit{
  searchTerm = new FormControl('');
  loadingSearch: boolean = false;
  allRetailers!: Retailer[];
  selectedRetailer!: Retailer;
  filteredRetailers!: Observable<Retailer[]>;

  dataSource!: MatTableDataSource<LeaseSpaceForTerm>;
  displayedColumns = ['select', 'id', 'spaceName', 'size'];

  edit = false;
  header: string = '';
  lease!: LeaseSummary;

  availableSpace = 0;

  selection = new SelectionModel<LeaseGridSpace>(true, []);

  offering!: PageOffering;
  retailerName?: string | null;
  retailerId?: number | null;

  existingRetailer = true;

  subLeaseForm: FormGroup;
  parentLease: LeaseSummary;

  leaseRequest!: SubLeaseRequest;
  userCreated = false;
  retailerCreated = false;

  userId!: number;
  errMessage = '';

  constructor(public dialogRef: MatDialogRef<CreateASubLeaseDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private retailersService: RetailersService,
              public loader: LoadingService,
              private subLeaseService: LeaseSubLeasesService,
              private userService: ConsoleUserService,
              private createARetailerService: CreateARetailerService) {
    this.parentLease = this.data.parentLease;

    this.edit = data.edit;
    this.header = data.header;
    this.lease = data.lease;
    this.offering = data.offering;

    if (data.selectedRetailer) {
      this.selectedRetailer = data.selectedRetailer;
      this.retailerName = this.selectedRetailer.companyName;
      this.retailerId = this.selectedRetailer.id!;
    } else if (data.retailerName) {
      this.retailerName = data.retailerName;
    }
    if (this.retailerName) {
      this.searchTerm.setValue(this.retailerName);
    }

    this.subLeaseForm = new FormGroup({
      firstName: new FormControl(null, Validators.required),
      lastName: new FormControl(null, Validators.required),
      email: new FormControl(null, [Validators.required, Validators.email]),
      companyName: new FormControl(null, Validators.required),
      industryTypes: new FormControl([], Validators.required),
      startDate: new FormControl(null, [Validators.required]),
      endDate: new FormControl(null, [Validators.required]),
      occupiedSpace: new FormControl(null, [
        Validators.required,
        Validators.min(1),
        (control: AbstractControl) => Validators.max(this.availableSpace)(control)
      ])
    })

    if (this.lease && this.lease.leaseStartDate && this.lease.leaseEndDate) {
      this.subLeaseForm.get('startDate')?.patchValue(new Date(this.lease.leaseStartDate));
      this.subLeaseForm.get('endDate')?.patchValue(new Date(this.lease.leaseEndDate));
    }

  }

  ngOnInit() {
    // this.loadIndustries();
    this.loadRetailers();
  }

  ngAfterViewInit() {
    this.subLeaseForm.get('endDate')?.valueChanges.subscribe(change => {
      if (change) this.loadAvailableSpace();
    })
  }

  loadRetailers() {
    this.loadingSearch = true;
    this.retailersService.getAll(
      {
        curated: ApplicationAssessmentStatus.APPROVED,
        tenantType: TenantType.TENANT
      }
    )
      .subscribe({
      next: value => {
        this.allRetailers = value.filter(ret => ret.id !== null && ret.id !== undefined && ret.id != this.parentLease.retailerId)
          .sort((a, b) => a.id! - b.id!);
        this.loadingSearch = false;

        this.filteredRetailers = this.searchTerm.valueChanges.pipe(
          startWith(''),
          map((value: string | Retailer | null) => {
            const name = typeof value == 'string' ? value : value?.companyName;
            return (name && name.trim().length > 0) ? this._filter(name as string) : this.allRetailers.slice();
          }),
        );
      },
      error: err => {
        console.log(err);
        this.loadingSearch = false;
      }
    })
  }

  loadAvailableSpace() {
    this.subLeaseService.getAvailableSpaceForDateRange(
      this.parentLease.uuid,
      null,
      null,
      this.dateUtils.displayShortDate(this.subLeaseForm.get('startDate')?.value)!,
      this.dateUtils.displayShortDate(this.subLeaseForm.get('endDate')?.value)
    ).subscribe({
      next: value => {
        this.availableSpace = value;
      },
      error: err => console.log(err)
    })
  }

  submitLease() {
    this.leaseRequest = {
      retailerId: this.selectedRetailer ? this.selectedRetailer.id! : null,
      offeringUuid: this.offering.offeringUuid,
      subLeaseUuid: null,
      spaceUuid: null,
      leaseUuid: this.parentLease.uuid,
      occupiedSpace: this.subLeaseForm.get('occupiedSpace')?.value,
      leaseStartDate: this.dateUtils.displayShortDate(this.subLeaseForm.get('startDate')?.value)!,
      leaseEndDate: this.dateUtils.displayShortDate(this.subLeaseForm.get('endDate')?.value)!,
    };

    if (this.existingRetailer) {
      this.createLease();
    } else {
      if (this.retailerCreated && this.userCreated) {
        this.createLease();
      } else {
        if (this.userCreated) {
          this.registerRetailer()
            .then(ret => {
              if (ret) this.createLease();
            })
        } else {
          this.registerUser()
            .then(res => {
              if (res) this.registerRetailer()
                .then(ret => {
                  if (ret) this.createLease();
                });
            })
        }
      }
    }
  }

  registerUser() {
    const newUser = new RegisterUserRequest({
      firstname: this.subLeaseForm.get('firstName')?.value,
      lastname: this.subLeaseForm.get('lastName')?.value,
      email: this.subLeaseForm.get('email')?.value,
    })

    return lastValueFrom(this.userService.registerNewPortalUser(newUser))
      .then(res => {
        this.userCreated = true;
        this.userId = res.id!;
        return true;
      })
      .catch((err) => {
        this.userCreated = false;
        switch (err.status) {
          case 400:
            if ('ValidationException' === err.headers.get('error_code')) {
              this.errMessage = '' + err.headers.get('error_message');
            }
            break;
          default:
            this.errMessage = 'An unexpected error occurred, please try again';
        }
        console.log(err);
        return false;
      })
  }

  registerRetailer() {
    const newRetailer: RegisterRetailer = {
      companyName: this.subLeaseForm.get('companyName')?.value,
      userId: this.userId,
      industries: this.subLeaseForm.get('industryTypes')?.value
    }

    return lastValueFrom(this.createARetailerService.registerNewRetailer(newRetailer))
      .then(res => {
        this.leaseRequest.retailerId = res.id!;
        console.log('Retailer created');
        return true;
      })
      .catch(err => {
        console.log('Error');
        console.log(err);
        return false;
      })
  }

  createLease() {
    this.subLeaseService.createSubLease(this.leaseRequest).subscribe({
      next: value => {
        this.openLeaseViewTab(value.uuid, value.occupancyType!, value.agreementType!);
        this.close();
      },
      error: err => console.log(err)
    });
  }

  displayFn(value: Retailer) {
    if (value) {
      return value.id && value.companyName ? `${value.id}. ${value.companyName}` : '';
    }
    return '';
  }

  selectRetailer(event: MatAutocompleteSelectedEvent) {
    this.selectedRetailer = event.option.value;
  }

  close() {
    this.dialogRef.close();
  }

  openLeaseViewTab(id: string, occupancyType: OccupancyType, agreementType: AgreementType) {
    const payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(id),
      pageHeader: this.offering.label,
      data: { id, occupancyType, agreementType },
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-schedule',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  disableRetailerSelection(): boolean {
    return !!this.lease && !!this.lease.retailerId && !!this.lease.retailerCompanyName;
  }

  getPlaceHolder(): string {
    if (this.lease) {
      return `${this.lease.retailerId}. ${this.lease.retailerCompanyName}`;
    } else if (this.retailerName && this.retailerId) {
      return `${this.retailerId}. ${this.retailerName}`;
    }
    return 'Enter a retailer company name';
  }

  getErrorMessage(formField: string) {
    if (this.subLeaseForm.get(formField)!.hasError('required')) return 'You must enter a value';
    if (formField == 'email' && this.subLeaseForm.get(formField)!.hasError('email')) return 'Not a valid email';

    if (formField == 'occupiedSpace') {
      if (this.subLeaseForm.get(formField)!.hasError('min')) return 'You must enter a value above 0';
      if (this.subLeaseForm.get(formField)!.hasError('max')) return 'There is not sufficient available space for the chosen dates';
    }
    return null;
  }

  private _filter(name: string): Retailer[] {
    const filterValue = name.toLowerCase();
    return this.allRetailers.filter(option => option.companyName.toLowerCase().includes(filterValue));
  }
}
